import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Notification, PaginatedResponse } from '@coffeeplexity/shared';
import {
  notificationApi,
  NotificationPreferences,
} from '../../services/api/notificationApi';

interface NotificationState {
  notifications: Notification[];
  preferences: NotificationPreferences | null;
  unreadCount: number;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;
  hasMore: boolean;
  currentPage: number;
}

const initialState: NotificationState = {
  notifications: [],
  preferences: null,
  unreadCount: 0,
  isLoading: false,
  isUpdating: false,
  error: null,
  hasMore: true,
  currentPage: 1,
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async (
    {
      page = 1,
      limit = 20,
      unreadOnly = false,
    }: { page?: number; limit?: number; unreadOnly?: boolean },
    { rejectWithValue }
  ) => {
    try {
      return await notificationApi.getNotifications(page, limit, unreadOnly);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch notifications'
      );
    }
  }
);

export const fetchNotificationPreferences = createAsyncThunk(
  'notifications/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      return await notificationApi.getPreferences();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          'Failed to fetch notification preferences'
      );
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk(
  'notifications/updatePreferences',
  async (
    preferences: Partial<NotificationPreferences>,
    { rejectWithValue }
  ) => {
    try {
      return await notificationApi.updatePreferences(preferences);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          'Failed to update notification preferences'
      );
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await notificationApi.markAsRead(notificationId);
      return notificationId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to mark notification as read'
      );
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      await notificationApi.markAllAsRead();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          'Failed to mark all notifications as read'
      );
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationApi.getUnreadCount();
      return response.count;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch unread count'
      );
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearNotifications: state => {
      state.notifications = [];
      state.currentPage = 1;
      state.hasMore = true;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex(n => n.id === action.payload);
      if (index !== -1) {
        const notification = state.notifications[index];
        if (!notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Notifications
      .addCase(fetchNotifications.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        const { items, has_next, page } = action.payload;

        if (page === 1) {
          state.notifications = items;
        } else {
          state.notifications.push(...items);
        }

        state.hasMore = has_next;
        state.currentPage = page;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Preferences
      .addCase(fetchNotificationPreferences.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = action.payload;
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Preferences
      .addCase(updateNotificationPreferences.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.preferences = action.payload;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Mark as Read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(
          n => n.id === action.payload
        );
        if (notification && !notification.read) {
          notification.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })

      // Mark All as Read
      .addCase(markAllNotificationsAsRead.fulfilled, state => {
        state.notifications.forEach(notification => {
          notification.read = true;
        });
        state.unreadCount = 0;
      })

      // Fetch Unread Count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      });
  },
});

export const {
  clearError,
  clearNotifications,
  addNotification,
  removeNotification,
} = notificationSlice.actions;

// Selectors
export const selectNotifications = (state: {
  notifications: NotificationState;
}) => state.notifications.notifications;
export const selectNotificationPreferences = (state: {
  notifications: NotificationState;
}) => state.notifications.preferences;
export const selectUnreadCount = (state: {
  notifications: NotificationState;
}) => state.notifications.unreadCount;
export const selectNotificationLoading = (state: {
  notifications: NotificationState;
}) => state.notifications.isLoading;
export const selectNotificationUpdating = (state: {
  notifications: NotificationState;
}) => state.notifications.isUpdating;
export const selectNotificationError = (state: {
  notifications: NotificationState;
}) => state.notifications.error;
export const selectHasMoreNotifications = (state: {
  notifications: NotificationState;
}) => state.notifications.hasMore;

export default notificationSlice.reducer;
